
import { Component, Mixins } from "vue-property-decorator";
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import { namespace, Getter } from "vuex-class";

import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";

import AlarmActionsMixin from "@/mixins/AlarmActionsMixin";
import { extractPropertyFromXml, extractPropertyFromXmlWithContainsCheck } from "@/utils/string-utils";
import { Datetime } from "vue-datetime";
import { FeaturesList } from "@/store/types";
import { get } from "lodash";
import { parse } from "qs";
import { Pref, PrefTypeRequest, PrefTypesEnum } from "@/store/settings/types";

const SettingsStore = namespace("settings");
interface ArmSettingsMeta {
	newState: boolean;
	dateTimeRevert?: string;
}

@Component({
	components: {
		"area-tree-select": AreaTreeSelect,
		"date-time": Datetime
	},
	validations: {
		currentAlarmAction: { objectID: { required } },
		hasDataChanged: {
			required: requiredIf(function() {
				return (
					this.currentAlarmAction.objectID === this.originalObjectId &&
					this.originalSettingsMeta === this.currentAlarmAction.settingsMeta
				);
			})
		}
	}
})
export default class ActionSetupArmSite extends Mixins(AlarmActionsMixin, validationMixin) {
	private originalArmStatus: boolean;
	private originalObjectId: number = null;
	private originalSettingsMeta: string = null;
	private originalHours: number = 0;
	private originalMinutes: number = 0;
	private hours?: number = 0;
	private minutes?: number = 0;

	@Getter("getFeaturesList") featuresList: FeaturesList;

	@SettingsStore.Action getPrefsFromList: (prefEnumList: PrefTypeRequest[]) => void;
	@SettingsStore.Getter getPrefBoolValue: (prefType: PrefTypeRequest) => boolean;

	private DateTimerevertchangeSiteArmStatus: number = 315;

	private settingsMeta: ArmSettingsMeta = {
		newState: false,
		dateTimeRevert: ""
	};

	public created(): void {
		if (this.isEditing) {
			this.setStateForEditing();
		} else {
			this.settingsMeta.newState = false;
		}

		this.currentAlarmAction.settingsMeta = JSON.stringify(this.settingsMeta);
		this.originalSettingsMeta = this.currentAlarmAction.settingsMeta;
	}

	/**
	 * Updates the settingsMeta object upon a change made in UI
	 */
	private async handleSettingsMetaChanged(): Promise<void> {
		this.timeConvertHoursAndMinutesToMinutes(this.hours, this.minutes);
		//API is expecting JSON for this field
		this.currentAlarmAction.settingsMeta = JSON.stringify(this.settingsMeta);

		await this.getPrefsFromList([this.PrefOfInterest]);
	}

	/**
	 * Calculates minutes to hours and minutes
	 * @param n minutes passed in as a number
	 */
	private timeConvertMinutestoHoursAndminutes(n: number) {
		if (!isNaN(n)) {
			var num = n;
			var hours = num / 60;
			var rhours = Math.floor(hours);
			var minutes = (hours - rhours) * 60;
			var rminutes = Math.round(minutes);
			this.hours = rhours;
			this.minutes = rminutes;
		}
	}

	/**
	 * Converts hours and minutes to minutes
	 * @param hours Hours passed in as a number.
	 * @param minutes Minutes passed in as a number
	 */
	private timeConvertHoursAndMinutesToMinutes(hours: number, minutes: number) {
		//if blank from db then it will be "" instead of 0 - this allows correct comparison if anything is changed
		if (hours === 0 && minutes === 0) {
			this.settingsMeta.dateTimeRevert = "";
		} else {
			var calMinutes = hours * 60;
			var total = calMinutes + minutes;
			this.settingsMeta.dateTimeRevert = total.toString();
		}
	}

	/**
	 *  Get the initial values for object passed in for comparison and extract data from xml settingsMeta
	 */
	private setStateForEditing(): void {
		this.originalObjectId = this.currentAlarmAction.objectID;
		this.settingsMeta.newState =
			extractPropertyFromXml(this.currentAlarmAction.settingsMeta, "newstate") === String(true);
		if (this.isDisarmAreaByActionForPeriodOn) {
			var minutes = parseInt(
				extractPropertyFromXmlWithContainsCheck(this.currentAlarmAction.settingsMeta, "DateTimeRevert")
			);
			if (!isNaN(minutes)) {
				this.timeConvertMinutestoHoursAndminutes(minutes);
				this.settingsMeta.dateTimeRevert = `${minutes}`;
			}
		}
		this.originalArmStatus = this.settingsMeta.newState;
		this.originalHours = this.hours;
		this.originalMinutes = this.minutes;
	}

	/**
	 *  Checks for whether the feature is disabled or not
	 */
	private get isDisarmAreaByActionForPeriodOn(): boolean {
		return get(this.featuresList, ["AlarmSetup", "CanDisarmAreaForPeriodByResponseAction"]);
	}

	/**
	 *  Checks for whether the pref is disabled or not.
	 */
	private get isDateTimerevertchangeSiteArmStatusOn(): boolean {
		return this.getPrefBoolValue(this.PrefOfInterest);
	}

	private get PrefOfInterest(): PrefTypeRequest {
		return {
			groupID: this.currentAlarmAction?.objectID ?? null,
			prefTypeID: PrefTypesEnum.DateTimerevertchangeSiteArmStatus
		} as PrefTypeRequest;
	}

	/**
	 *  Clears the time values if not already set to zero
	 */
	private clearTime(): void {
		if (this.hours > 0 || this.minutes > 0) {
			this.hours = 0;
			this.minutes = 0;
			this.handleSettingsMetaChanged();
		}
	}
}
